import { AxiosError } from 'axios'
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { useConfig } from '../../config'
import { setToken, useLocale, useTranslation } from '../../utils'

import { pushDangerNotification, pushSuccessNotification } from '../snackbar'
import { userApi } from './user.api'
import { USER_KEY } from './user.constants'
import {
  ProfileChangeEmailRequest,
  ProfileChangePasswordRequest,
  ProfileConfirmEmailRequest,
  ProfileConfirmEmailResponse,
  ProfileUser,
  SaveProfileUser
} from './user.types'

export const useMutationLogout = (
  options?: UseMutationOptions<unknown, AxiosError>
) => {
  const queryClient = useQueryClient()
  const locale = useLocale()

  const mutation = useMutation(() => userApi.logout(locale), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(USER_KEY)
      queryClient.clear()

      options?.onSuccess?.(data, variables, context)
    }
  })

  return mutation
}

export const useMutationSaveProfileUser = (
  options?: UseMutationOptions<ProfileUser, AxiosError, SaveProfileUser>
) => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const mutation = useMutation(
    async (requestBody) => await userApi.saveProfile(requestBody, locale),
    {
      onError: (error) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
      },
      onSuccess: (data) => {
        dispatch(pushSuccessNotification(translate?.['success-edit-profile']))
        queryClient.setQueryData<ProfileUser>([USER_KEY, locale], data)
      },
      ...options
    }
  )

  return mutation
}

export const useMutationChangeEmailProfileUser = (
  options?: UseMutationOptions<unknown, AxiosError, ProfileChangeEmailRequest>
) => {
  const dispatch = useDispatch()
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const mutation = useMutation(
    async (requestBody) => await userApi.changeEmail(requestBody, locale),
    {
      onError: (error) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
      },
      onSuccess: () => {
        dispatch(pushSuccessNotification(translate?.['send-edit-alert-mail']))
      },
      ...options
    }
  )

  return mutation
}

export const useMutationChangePasswordProfileUser = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    ProfileChangePasswordRequest
  >
) => {
  const dispatch = useDispatch()
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const mutation = useMutation(
    async (requestBody) => await userApi.changePassword(requestBody, locale),
    {
      onError: (error) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
      },
      onSuccess: () => {
        dispatch(
          pushSuccessNotification(translate?.['password-changed-successfully'])
        )
      },
      ...options
    }
  )

  return mutation
}

export const useMutationConfirmEmailProfileUser = (
  options?: UseMutationOptions<
    ProfileConfirmEmailResponse,
    AxiosError,
    ProfileConfirmEmailRequest
  >
) => {
  const dispatch = useDispatch()
  const config = useConfig()
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const mutation = useMutation(
    async (requestBody) =>
      await userApi.confirmChangeEmail(requestBody, locale),
    {
      ...options,
      onError: (error, variables, context) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
        options?.onError?.(error, variables, context)
      },
      onSuccess: (response, variables, context) => {
        console.log(response.token, response.ttl, config.cookiesDomain)
        setToken(response.token, response.ttl, config.cookiesDomain)
        dispatch(
          pushSuccessNotification(translate?.['email-successfully-verified'])
        )
        options?.onSuccess?.(response, variables, context)
      }
    }
  )

  return mutation
}
